/**
 * Effort banding for chores, mirroring ChoreEffort/EffortBucket on the API side.
 *
 * The point of banding is decision-making: when you have five spare minutes the only
 * question that matters is "what fits?", not whether something takes 18 or 22 minutes.
 * Raw minutes also invite agonising over estimates, which is its own form of avoidance.
 */

export const QUICK_WIN_MAX_MINUTES = 2;
export const SHORT_MAX_MINUTES = 15;
export const MEDIUM_MAX_MINUTES = 60;

export const EFFORT_BUCKETS = ['quickWin', 'short', 'medium', 'big', 'unknown'];

export const EFFORT_META = {
  quickWin: { icon: '⚡', className: 'effort-quick-win' },
  short: { icon: '🍵', className: 'effort-short' },
  medium: { icon: '🧹', className: 'effort-medium' },
  big: { icon: '🛠️', className: 'effort-big' },
  unknown: { icon: '❓', className: 'effort-unknown' }
};

/** Minute values offered as one-tap choices when estimating. */
export const ESTIMATE_CHOICES = [1, 2, 5, 10, 15, 30, 60, 120, 240];

export function bucketFor(estimatedMinutes) {
  if (estimatedMinutes === null || estimatedMinutes === undefined) return 'unknown';
  if (estimatedMinutes <= QUICK_WIN_MAX_MINUTES) return 'quickWin';
  if (estimatedMinutes <= SHORT_MAX_MINUTES) return 'short';
  if (estimatedMinutes <= MEDIUM_MAX_MINUTES) return 'medium';
  return 'big';
}

/**
 * Sorts so the easiest thing to start is always first: quick wins, then by estimate.
 * Unestimated work sorts last rather than first — it can't be judged as "fits right now",
 * and putting unknowns at the top would recreate the wall of undifferentiated tasks.
 */
export function byEffortAscending(a, b) {
  const aMin = a.estimatedMinutes;
  const bMin = b.estimatedMinutes;
  const aUnknown = aMin === null || aMin === undefined;
  const bUnknown = bMin === null || bMin === undefined;
  if (aUnknown && bUnknown) return 0;
  if (aUnknown) return 1;
  if (bUnknown) return -1;
  return aMin - bMin;
}

/** "45 min" / "1 h 30 min" — hours only once it stops reading as a number of minutes. */
export function formatMinutes(minutes, t) {
  if (minutes === null || minutes === undefined) return t('chores.effort.noEstimate');
  if (minutes < 60) return t('chores.effort.minutesShort', { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (rest === 0) return t('chores.effort.hoursShort', { count: hours });
  return `${t('chores.effort.hoursShort', { count: hours })} ${t('chores.effort.minutesShort', { count: rest })}`;
}

export function groupByEffort(chores) {
  const groups = { quickWin: [], short: [], medium: [], big: [], unknown: [] };
  chores.forEach(chore => groups[bucketFor(chore.estimatedMinutes)].push(chore));
  EFFORT_BUCKETS.forEach(key => groups[key].sort(byEffortAscending));
  return groups;
}
